import { useEffect, useState } from 'react';

import { t } from 'i18next';
import { Info } from 'lucide-react';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { ButtonLoadable } from '@/components/ui/button-loadable';
import { getTimeDifferenceString } from '@/helpers';
import { cn } from '@/lib/utils';

export interface RefreshDataProps {
  onRefresh: () => void;
  isPending: boolean;
  dataUpdatedAt: Date | number | null | undefined;
  disabled?: boolean;
  description: string | React.ReactElement;
  buttonTitle: string;
  containerClassName?: string;
}

export const RefreshData = ({
  description,
  buttonTitle,
  isPending,
  disabled,
  onRefresh,
  dataUpdatedAt,
  containerClassName,
}: RefreshDataProps) => {
  const [timeDiff, setTimeDiff] = useState<string | null>(getTimeDifferenceString(dataUpdatedAt));

  useEffect(() => {
    if (isPending) {
      setTimeDiff(null);
      return;
    }

    setTimeDiff(getTimeDifferenceString(dataUpdatedAt));

    const interval = setInterval(() => {
      setTimeDiff(getTimeDifferenceString(dataUpdatedAt));
    }, 1000);

    return () => clearInterval(interval);
  }, [dataUpdatedAt, isPending]);

  return (
    <Alert className={cn('border-border bg-muted/50', containerClassName)}>
      <AlertDescription className="w-full min-w-0">
        {/* Responsive layout with specific breakpoint behaviors */}
        <div className="flex min-w-0 flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="min-w-0 flex-1 lg:pr-4">
            <div className="flex items-center gap-3">
              <Info className="h-4 w-4 flex-shrink-0 text-foreground" />
              <div className="min-w-0 flex-1 whitespace-pre-line text-xs leading-4 text-foreground">
                {description}
              </div>
            </div>
          </div>

          {/* XL/L: Time and button horizontal on right side */}
          {/* S/M: Button under text, time to right of button */}
          {/* XS: Button under text, time under button */}
          <div className="min-w-0 lg:flex-shrink-0">
            {/* XL/L layout: horizontal time+button */}
            <div className="hidden lg:flex lg:flex-row lg:items-center lg:gap-3">
              <span className="overflow-hidden text-ellipsis whitespace-nowrap text-xs text-muted-foreground">
                {timeDiff &&
                  t('common:refreshData.refreshStatus', {
                    time: timeDiff,
                  })}
              </span>
              <ButtonLoadable
                size="sm"
                variant="default"
                onClick={onRefresh}
                showRefreshIcon={true}
                isLoading={isPending}
                disabled={disabled}
                className="flex-shrink-0"
              >
                {buttonTitle}
              </ButtonLoadable>
            </div>

            {/* S/M layout: button first, then time to its right */}
            <div className="hidden min-w-0 sm:flex sm:flex-row sm:items-center sm:gap-3 lg:hidden">
              <ButtonLoadable
                size="sm"
                variant="default"
                onClick={onRefresh}
                isLoading={isPending}
                showRefreshIcon={true}
                disabled={disabled}
                className="flex-shrink-0"
              >
                {buttonTitle}
              </ButtonLoadable>
              <span className="overflow-hidden text-ellipsis whitespace-nowrap text-xs text-muted-foreground">
                {timeDiff &&
                  t('common:refreshData.refreshStatus', {
                    time: timeDiff,
                  })}
              </span>
            </div>

            {/* XS layout: button and time stacked vertically */}
            <div className="flex min-w-0 flex-col gap-3 sm:hidden">
              <ButtonLoadable
                size="sm"
                variant="default"
                onClick={onRefresh}
                showRefreshIcon={true}
                isLoading={isPending}
                disabled={disabled}
                className="self-start"
              >
                {buttonTitle}
              </ButtonLoadable>
              <span className="overflow-hidden text-ellipsis text-xs text-muted-foreground">
                {timeDiff &&
                  t('common:refreshData.refreshStatus', {
                    time: timeDiff,
                  })}
              </span>
            </div>
          </div>
        </div>
      </AlertDescription>
    </Alert>
  );
};
